import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useAppDispatch } from 'app/hooks';
import Tooltip from 'components/layout/tooltip';
import { DesignRuleValidationIssue } from 'data/types';
import { setPendingFix, setPendingMultiFix } from 'features/chatbot';
import { setShowChatPanel } from 'features/ui';
import { useState } from 'react';
import { isErrorSeverity } from 'utils/validation';

type DesignRulePanelProps = {
  issues: DesignRuleValidationIssue[];
};

function issueKey(issue: DesignRuleValidationIssue): string {
  const names = issue.failures
    .map((f) => f.condenserName || f.zoneName)
    .filter(Boolean);
  return `${issue.code}-${names.join(',')}`;
}

function buildFixPayload(issue: DesignRuleValidationIssue) {
  const failureContext = issue.failures
    .map((f) => {
      if (f.condenserName && f.condenserUniqueId) {
        return `[Condenser: ${f.condenserName} (${f.condenserUniqueId})]`;
      }
      if (f.zoneName && f.zoneUniqueId) {
        return `[Zone: ${f.zoneName} (${f.zoneUniqueId})]`;
      }
      return '';
    })
    .filter(Boolean)
    .join(' ');

  const displayFailureContext = issue.failures
    .map((f) => {
      if (f.condenserName) return `[Condenser: ${f.condenserName}]`;
      if (f.zoneName) return `[Zone: ${f.zoneName}]`;
      return '';
    })
    .filter(Boolean)
    .join(' ');

  const actionableRecs = issue.recommendations.filter(
    (r) => r.active && r.typeCode === 'actionable'
  );

  return {
    issueName: issue.name,
    issueMessage: issue.message,
    failureContext,
    displayFailureContext,
    recommendations: actionableRecs
  };
}

export default function DesignRulePanel({
  issues
}: Readonly<DesignRulePanelProps>) {
  const dispatch = useAppDispatch();
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());

  if (issues.length === 0) return null;

  const actionableIssues = issues.filter((issue) =>
    issue.recommendations.some((r) => r.active && r.typeCode === 'actionable')
  );

  const toggleCheck = (key: string) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleFixWithAI = () => {
    const selected = issues.filter((issue) => checkedKeys.has(issueKey(issue)));
    if (selected.length === 0) return;

    if (selected.length === 1) {
      dispatch(setPendingFix(buildFixPayload(selected[0])));
    } else {
      dispatch(setPendingMultiFix(selected.map(buildFixPayload)));
    }

    dispatch(setShowChatPanel(true));
    setCheckedKeys(new Set());
  };

  const sortedIssues = [...issues].sort((a, b) => {
    const aIsError = isErrorSeverity(a.severity);
    const bIsError = isErrorSeverity(b.severity);
    if (aIsError && !bIsError) return -1;
    if (!aIsError && bIsError) return 1;
    return 0;
  });

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto'>
        {sortedIssues.map((issue) => {
          const error = isErrorSeverity(issue.severity);
          const borderColor = error ? 'border-red-200' : 'border-amber-200';
          const bgColor = error ? 'bg-red-50' : 'bg-amber-50';
          const textColor = error ? 'text-red-700' : 'text-amber-700';
          const nameColor = error ? 'text-red-800' : 'text-amber-800';
          const Icon = error ? ExclamationCircleIcon : ExclamationTriangleIcon;

          const failureNames = issue.failures
            .map((f) => f.condenserName || f.zoneName)
            .filter(Boolean);

          const key = issueKey(issue);
          const hasActionableRecs = issue.recommendations.some(
            (r) => r.active && r.typeCode === 'actionable'
          );

          return (
            <div
              key={key}
              className={`rounded-lg border ${borderColor} ${bgColor} p-3`}
            >
              <div className={`flex items-start gap-2 ${textColor}`}>
                {hasActionableRecs && (
                  <input
                    type='checkbox'
                    checked={checkedKeys.has(key)}
                    onChange={() => toggleCheck(key)}
                    className='mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500'
                  />
                )}
                <Icon className='h-5 w-5 shrink-0' />
                <div className='flex min-w-0 flex-col gap-1'>
                  {failureNames.length > 0 && (
                    <span className={`text-sm font-semibold ${nameColor}`}>
                      {failureNames.join(', ')}
                    </span>
                  )}
                  <div className='relative'>
                    <span className='text-sm'>{issue.message}</span>
                    <Tooltip>
                      <div className='max-w-[280px]'>
                        <div className='mb-1 font-semibold'>{issue.name}</div>
                        <div className='text-xs'>{issue.reasoning}</div>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {actionableIssues.length > 0 && (
        <div className='shrink-0 pt-3'>
          <button
            type='button'
            onClick={handleFixWithAI}
            disabled={checkedKeys.size === 0}
            className='flex w-full items-center justify-center gap-1.5 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-40'
          >
            <SparklesIcon className='h-4 w-4' />
            Fix with AI ({checkedKeys.size})
          </button>
        </div>
      )}
    </div>
  );
}
